import { Cast, Film } from '../services';
import { Event, Movie, MovieTheater, Organization, Person, WebSite } from 'schema-dts';

import { CinemaModel } from '../services/cinemas';
import getConfig from 'next/config';
import { jsonLdScriptProps } from 'react-schemaorg';
import { locales } from '@/lang/locales';
import { toISO8601Duration } from './format-minutes-to-hours';

/* Getting the public runtime configuration from the next.config.js file. */
const { publicRuntimeConfig } = getConfig();

enum Types {
  Movie = 'Movie',
  Person = 'Person',
  ScreeningEvent = 'ScreeningEvent',
  MovieTheater = 'MovieTheater',
}

/**
 * Entity ids.
 *
 * Every page that mentions a cinema, a film or the chain has to mint the *same* `@id` for it,
 * or the references below point at nothing and each page ends up declaring its own private,
 * half-populated copy of the entity. So these are derived from data every payload carries —
 * the locale and the slug — and never from `canonicalURL`: the CMS only fills that field in on
 * the entity's own detail response, so a movie page listing seven cinemas has no access to it.
 *
 * The page's own `url` still prefers `canonicalURL`. Identity and address are different jobs.
 */
const toPathLocale = (locale?: string): string =>
  locale === locales.AR ? locales.AR : locales.EN;

export const cinemaPageUrl = (uniqueName: string, locale?: string): string =>
  `${publicRuntimeConfig.nextPublicBaseUrl}${toPathLocale(locale)}/cinemas/${uniqueName}`;

export const cinemaSchemaId = (uniqueName: string, locale?: string): string =>
  `${cinemaPageUrl(uniqueName, locale)}#cinema`;

export const moviePageUrl = (uniqueName: string, locale?: string): string =>
  `${publicRuntimeConfig.nextPublicBaseUrl}${toPathLocale(locale)}/movies/${uniqueName}`;

export const movieSchemaId = (uniqueName: string, locale?: string): string =>
  `${moviePageUrl(uniqueName, locale)}#movie`;

export const organizationSchemaId = (): string =>
  `${publicRuntimeConfig.nextPublicBaseUrl}#organization`;

/**
 * It takes an array of `Cast` objects and returns an array of `Person` objects
 * @param {Cast[]} casts - Cast[]
 * @returns An array of objects with the type Person.
 */
const getActors = (casts: Cast[]): Person[] => {
  return casts.map(cast => {
    return {
      '@type': Types.Person,
      'name': `${cast.firstName} ${cast.lastName}`,
    };
  });
};

/**
 * It returns a React component that renders two JSON-LD scripts.
 * @returns A React Fragment with two script tags.
 */
export const buildOrganizationSchemaComponent = () => {
  return (
    <>
      <script
        {...jsonLdScriptProps<Organization>({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': organizationSchemaId(),
          'name': publicRuntimeConfig.siteName,
          'alternateName': 'muvi',
          url: publicRuntimeConfig.nextPublicBaseUrl,
          logo: `${publicRuntimeConfig.nextPublicBaseUrl}icons/logos/muvi-logo.svg`,
          'sameAs': [
            'https://www.instagram.com/muvi_cinemas/',
            'https://twitter.com/muvicinemas',
            'https://www.facebook.com/muvicinemas/',
            'https://www.snapchat.com/add/muvicinemas',
          ],
        })}
      />
      <script
        {...jsonLdScriptProps<WebSite>({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          'name': publicRuntimeConfig.siteName,
          'alternateName': 'muvi',
          'url': publicRuntimeConfig.nextPublicBaseUrl,
        })}
      />
    </>
  );
};

/**
 * It takes a movie and a list of cinemas and returns a React component that renders a JSON-LD script
 * tag with the movie and cinema data.
 * @param {Film} movie - Film - this is the movie object that contains all the movie details
 * @param {CinemaModel[]} cinemas - CinemaModel[]
 * @param {string} locale - the rendering locale, used to mint entity ids
 * @returns A React component.
 */
export const buildEventSchemaComponent = (
  movie: Film,
  cinemas: CinemaModel[],
  locale?: string
) => {
  /* A stable id so the ScreeningEvent can reference this Movie instead of declaring a second,
     half-populated one. Google treats a bare `@type: Movie` as its own entity and fails it for
     the fields it is missing. */
  const movieId = movieSchemaId(String(movie.uniqueName ?? movie.id), locale);
  /* Query string dropped for the same reason as on the cinema schema below. */
  const url = (
    movie.canonicalURL || moviePageUrl(String(movie.uniqueName ?? movie.id), locale)
  ).split(/[?#]/)[0];
  /* `descriptionTag` is blank for a handful of films in the CMS; the synopsis is the same
     fallback the page's meta description already uses. */
  const description = movie.descriptionTag || movie.synopsis;
  const duration = toISO8601Duration(movie.runtime);
  /* Trimmed to a plain date: schema.org wants a Date here, and the API sends a full timestamp. */
  const releaseDate = /^\d{4}-\d{2}-\d{2}/.exec(movie.releaseDate ?? '')?.[0];
  /* The earliest bookable showtime among the cinemas the page was rendered with (the first
     showtime date). An event without a start date is invalid, so when there is none, for a
     film that is only coming soon, the ScreeningEvent is left out rather than sent broken.
     ISO-8601 UTC strings compare correctly as plain strings. */
  const startDate = (cinemas ?? [])
    .flatMap(cinema => cinema.experiences ?? [])
    .flatMap(experience => experience.sessions ?? [])
    .filter(session => session.showtime && !session.isDisabled)
    .map(session => session.showtime)
    .sort()[0];

  return (
    <>
      <script
        {...jsonLdScriptProps<Movie>({
          '@context': 'https://schema.org',
          '@type': Types.Movie,
          '@id': movieId,
          'name': movie.title,
          'url': url,
          'description': description,
          ...(movie.casts?.length > 0 && { 'actor': getActors(movie.casts) }),
          ...(movie.directors?.length > 0 && {
            'director': getActors([movie.directors[0]]),
          }),
          'image': movie.image,
          ...(duration && { 'duration': duration }),
          ...(releaseDate && { 'dateCreated': releaseDate }),
          'genre': movie.genres?.map(genre => genre.name),
          'subtitleLanguage': movie.subtitle,
          'inLanguage': movie.movieLang,
          ...(movie.imdb && { 'sameAs': movie.imdb }),
        })}
      />
      {startDate && (
        <script
          {...jsonLdScriptProps<Event>({
            '@context': 'https://schema.org',
            '@type': Types.ScreeningEvent,
            'name': movie.title,
            'url': url,
            'description': description,
            ...(movie.image && { 'image': movie.image }),
            'startDate': startDate,
            'eventStatus': 'https://schema.org/EventScheduled',
            'eventAttendanceMode': 'https://schema.org/OfflineEventAttendanceMode',
            /* Each cinema is named *and* identified. The `@id` is what lets Google fold this
               mention into the full MovieTheater declared on the cinema's own page — the bare
               `{ @type, name }` these used to be were read as seven separate businesses with no
               address, phone or image. The name and url stay because, unlike `workPresented`
               above, the entity being referenced is not on this page: a lone `@id` here would
               leave the screening with a location it cannot describe. The city goes in for the
               same reason: it is what ties "<film> in <city>" to this screening. */
            'location': cinemas.map(cinema => ({
              '@type': Types.MovieTheater,
              ...(cinema.uniqueName && {
                '@id': cinemaSchemaId(cinema.uniqueName, locale),
                'url': cinemaPageUrl(cinema.uniqueName, locale),
              }),
              'name': `${cinema.name}`,
              ...(cinema.city?.name && {
                'address': {
                  '@type': 'PostalAddress' as const,
                  'addressLocality': cinema.city.name,
                  'addressCountry': 'SA',
                },
              }),
            })),
            'organizer': {
              '@type': 'Organization',
              '@id': organizationSchemaId(),
              'name': publicRuntimeConfig.siteName,
              'url': publicRuntimeConfig.nextPublicBaseUrl,
            },
            'workPresented': { '@id': movieId },
            'inLanguage': movie.movieLang,
            'videoFormat': 'Digital',
          })}
        />
      )}
    </>
  );
};

/**
 * It takes a cinema and returns a React component that renders a MovieTheater JSON-LD script tag.
 * This is what ties a cinema page to a real place: without it Google has the address, coordinates
 * and experiences on the page for a guest to read, but nothing it can parse.
 *
 * `telephone` and `openingHours` are deliberately omitted — the phone numbers on the model are not
 * live, and no opening-hours data exists on the API. MovieTheater is valid without both.
 * @param {CinemaModel} cinema - CinemaModel
 * @param {string} locale - the rendering locale, used to mint the entity id
 * @returns A React component.
 */
export const buildCinemaSchemaComponent = (cinema: CinemaModel, locale?: string) => {
  if (!cinema) return null;

  /* `url` is where the entity lives and so prefers the CMS's canonical; `@id` is what the
     movie pages reference and so has to be derivable without it. See the note on the id
     helpers above. */
  /* The CMS ships a query string on some canonicals (`…/hayat-mall?cityId=2`). `<link rel=
     "canonical">` drops it — see handleCanonicalUrl in components/seo/seo.tsx — so this has to
     drop it too, or the page and its own structured data name two different URLs. */
  const url = (cinema.canonicalURL || cinemaPageUrl(cinema.uniqueName, locale)).split(
    /[?#]/
  )[0];
  const streetAddress = [cinema.address1, cinema.address2].filter(Boolean).join(', ');
  const images = [cinema.image, cinema.coverUrl].filter(Boolean);
  /* lat/long arrive as strings; Number() keeps an invalid value out of the payload rather than
     emitting NaN. */
  const latitude = Number(cinema.lat);
  const longitude = Number(cinema.long);
  const hasGeo =
    Boolean(cinema.lat && cinema.long) && !isNaN(latitude) && !isNaN(longitude);

  return (
    <script
      {...jsonLdScriptProps<MovieTheater>({
        '@context': 'https://schema.org',
        '@type': Types.MovieTheater,
        '@id': cinemaSchemaId(cinema.uniqueName, locale),
        'name': cinema.name,
        'url': url,
        ...(cinema.description && { 'description': cinema.description }),
        ...(images.length > 0 && { 'image': images }),
        ...(streetAddress && {
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': streetAddress,
            ...(cinema.city?.name && { 'addressLocality': cinema.city.name }),
            'addressCountry': 'SA',
          },
        }),
        ...(hasGeo && {
          'geo': {
            '@type': 'GeoCoordinates',
            'latitude': latitude,
            'longitude': longitude,
          },
        }),
        ...(cinema.experiences?.length > 0 && {
          'amenityFeature': cinema.experiences
            .filter(experience => !experience.isDisabled)
            .map(experience => ({
              '@type': 'LocationFeatureSpecification' as const,
              'name': experience.name?.trim(),
              'value': true,
            })),
        }),
        /* Same id the homepage declares the chain under, so this is understood as a branch of
           that one Organization rather than a new one per cinema page. */
        'parentOrganization': {
          '@type': 'Organization',
          '@id': organizationSchemaId(),
          'name': publicRuntimeConfig.siteName,
          'url': publicRuntimeConfig.nextPublicBaseUrl,
        },
      })}
    />
  );
};
